import React from 'react';
import { 
  Compass, 
  TrendingUp, 
  BookOpen, 
  Leaf, 
  FileText, 
  Pill,
  Activity,
  BarChart3,
  Bell,
  User,
  Download,
  Newspaper
} from 'lucide-react';

const HowToUsePage = () => {
  const sections = [
    {
      icon: BarChart3,
      title: 'Dashboard',
      color: 'text-primary',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      description: 'Your health overview at a glance',
      points: [
        'View your most recent blood test results with trend indicators',
        'See how your key markers compare to reference ranges',
        'Track your wellbeing with the daily mood check-in',
        'Quick access to your nutrition and deficiency tracking'
      ]
    },
    {
      icon: TrendingUp,
      title: 'My Progress',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      description: 'Track your blood results over time',
      points: [
        'Add blood test results manually from your pathology reports',
        'View interactive charts showing your trends over 12, 24, or 36 months',
        'See your treatments overlaid on the timeline to spot correlations',
        'Export a PDF summary to share with your healthcare team'
      ]
    },
    {
      icon: Pill,
      title: 'Treatments',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      description: 'Manage your vitamins, supplements, and medications',
      points: [
        'Add vitamins, supplements, medications, or other treatments',
        'Record start and end dates to track duration',
        'View all your treatments on a visual timeline',
        'See which treatments overlap with changes in your blood results'
      ]
    },
    {
      icon: Activity,
      title: 'Symptoms',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      description: 'Log and monitor your symptoms',
      points: [
        'Record symptoms when they occur with severity levels',
        'Add notes about triggers, duration, or what helped',
        'Track patterns over time to discuss with your doctor',
        'View symptom history alongside your blood results'
      ]
    },
    {
      icon: Leaf,
      title: 'Integrative Health',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      description: 'Educational resources for holistic wellbeing',
      points: [
        'Browse evidence-based information on nutrition and lifestyle',
        'Learn about foods that may support your immune system',
        'Explore recipes and smoothies designed for health',
        'Read about food safety considerations during treatment'
      ]
    },
    {
      icon: Newspaper,
      title: 'News & Blog',
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      description: 'Stay informed with the latest CLL/SLL updates',
      points: [
        'Read curated news articles about CLL and SLL research',
        'Stay updated on new treatments and clinical trials',
        'Get insights from the latest medical publications',
        'Subscribe to receive notifications when new articles are posted'
      ]
    },
    {
      icon: BookOpen,
      title: 'Education',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      description: 'Learn about CLL/SLL and your health markers',
      points: [
        'What is CLL/SLL? - Understand your condition in plain language',
        'CLL vs SLL - Learn the difference between these related conditions',
        'Glossary - Look up medical terms from your reports',
        'Common Questions - Find answers to frequently asked questions'
      ]
    },
    {
      icon: FileText,
      title: 'My Ideas & Notes',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      description: 'Your personal space for reflection',
      points: [
        'Write down questions to ask your doctor at appointments',
        'Save notes about things you\'ve learned or want to remember',
        'Record your thoughts and reflections on your health journey',
        'Keep everything private and accessible only to you'
      ]
    },
    {
      icon: Bell,
      title: 'Alerts',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      description: 'Stay informed about important changes',
      points: [
        'Get notified when blood markers move outside reference ranges',
        'Receive alerts about significant changes in your results',
        'Choose to receive notifications via email or in-app',
        'Customize which alerts matter most to you'
      ]
    },
    {
      icon: Download,
      title: 'Export & Share',
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      description: 'Share your data with your healthcare team',
      points: [
        'Generate a PDF summary of your blood results and trends',
        'Include your treatments timeline in the report',
        'Choose the date range to export',
        'Print or email to your doctor before appointments'
      ]
    }
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Compass className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            How to Use This Platform
          </h1>
        </div>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 max-w-3xl">
          MyCBC helps you track, understand, and manage your health journey with CLL/SLL. Here's what each section of the platform does and how it can help you.
        </p>
      </div>

      {/* Getting Started */}
      <div className="bg-primary/10 border-l-4 border-primary p-6 rounded-r-xl mb-8">
        <p className="text-base md:text-lg font-medium text-slate-900 mb-2">
          Getting Started
        </p>
        <p className="text-sm md:text-base text-slate-700">
          Start by adding your most recent blood test results in <strong>My Progress</strong>. Then add any vitamins, supplements, or medications you're taking in <strong>Treatments</strong>. From there, explore the educational content and use the platform to track changes over time.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <div 
              key={index}
              className={`${section.bgColor} border ${section.borderColor} rounded-2xl p-6 md:p-8`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 bg-white rounded-xl shadow-sm flex-shrink-0`}>
                  <Icon className={`h-6 w-6 ${section.color}`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-1">
                    {section.title}
                  </h2>
                  <p className="text-sm md:text-base text-slate-600 mb-4">
                    {section.description}
                  </p>
                  <ul className="space-y-2">
                    {section.points.map((point, pointIndex) => (
                      <li 
                        key={pointIndex}
                        className="flex items-start gap-3 text-sm md:text-base text-slate-700"
                      >
                        <span className={`${section.color} flex-shrink-0 mt-0.5`}>•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tips Section */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mt-8">
        <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">
          Tips for Getting the Most Out of MyCBC
        </h3>
        <ul className="space-y-3 text-base text-slate-700">
          <li className="flex items-start gap-3">
            <span className="text-primary font-bold">1.</span>
            <span><strong>Add results after each blood test</strong> - The more data you add, the clearer your trends become.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary font-bold">2.</span>
            <span><strong>Keep treatments up to date</strong> - Record when you start or stop supplements so you can see if they correlate with changes.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary font-bold">3.</span>
            <span><strong>Use the PDF export before appointments</strong> - Share your trends with your healthcare team for more informed discussions.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary font-bold">4.</span>
            <span><strong>Write down questions</strong> - Use My Ideas to note things you want to ask your doctor.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-primary font-bold">5.</span>
            <span><strong>Check the Glossary</strong> - If you see a term you don't understand on your pathology report, look it up.</span>
          </li>
        </ul>
      </div>

      {/* Support Note */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8">
        <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-3">
          Need Help?
        </h3>
        <p className="text-base leading-relaxed text-slate-700">
          If you have questions about how to use the platform, check the <strong>Common Questions</strong> page in the Education section. For technical issues, you can reach out through the contact options in your profile settings.
        </p>
      </div>
    </div>
  );
};

export default HowToUsePage;
